import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { Car, CarCurrentAssignment, CarNoCarDepency } from "../../http/cars/types";
import { DefaultTask } from "../../http/defaultTasks/types";
import { useDueDateTypesApi } from "../../http/dueDateTypes/api";
import { DueDateType } from "../../http/dueDateTypes/types";
import { DueDate } from "../../http/dueDates/types";
import { Manufacturer } from "../../http/manufacturers/types";
import { User } from "../../http/users/types";
import { PaginationQueryWithFields } from "../../types";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import ModalDialog from "../ui/ModalDialog";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Switch from "../ui/Switch";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

const DUE_DATE_TYPE_FIELDS = ["id", "name", "versionNumber"] as const;

export interface DueDateFormBody {
  id: string;
  car: {
    id: string;
    currentPlateNumber: string;
    lastPlateNumber: string;
    vehicleIdentificationNumber: string;
    manufacturer: Manufacturer;
    model: string;
    registrationDate: string;
  };
  type: DueDateType;
  date: string;
  isSettled: boolean;
  visibleToUser: boolean;
  driver?: User;
}

type DueDateFormProps = {
  dueDate?: DueDate | null;
  onSubmit: (form: DueDateFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  carId?: string;
  defaultTask?: DefaultTask;
};

const DueDateFormNuovo: FC<DueDateFormProps> = ({
  dueDate,
  onSubmit,
  isDisabled,
  isLoading,
  carId,
  defaultTask,
}: DueDateFormProps) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();
  const typesApi = useDueDateTypesApi();
  const aborter = useRef(new AbortController());

  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);
  const [currentAssignment, setCurrentAssigment] = useState<CarCurrentAssignment>();
  const [isHelpVisibleOpen, setIsHelpVisibleOpen] = useState(false);

  const query: PaginationQueryWithFields<DueDateType> = {
    fields: DUE_DATE_TYPE_FIELDS,
  };

  const types = typesApi.useAdminDueDateTypes(query, aborter.current.signal);

  const loadCurrentAssigmment = async (carId: string) => {
    const currAss = await carsApi.getCurrentAssignment(carId, aborter.current.signal);
    setCurrentAssigment({
      carDriver: currAss.carDriver,
      otherCarAssignment: currAss.otherCarAssignment,
    });
  };

  useEffect(() => {
    const id = carId || dueDate?.car.id;
    !isLoading && id && loadCurrentAssigmment(id);
  }, [carId, dueDate?.car.id, isLoading]);

  const defaultValues = {
    car: currentAssignment?.carDriver?.car || dueDate?.car,
    type: defaultTask?.defaultDueDateType || dueDate?.type,
    date: dueDate?.date,
    isSettled: dueDate?.isSettled,
    visibleToUser: !!dueDate?.visibleToUser,
    driver: dueDate?.visibleToUser || currentAssignment?.carDriver?.driver,
  };

  return (
    <>
      <Form<DueDateFormBody>
        defaultValues={defaultValues}
        submitText={i18n.t.common.save}
        disabled={isDisabled}
        onSubmit={() => onSubmit}
        itemCreationInfo={dueDate}
      >
        {({ formState: { errors }, control, reset, setValue, watch }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [dueDate?.id, carId]);

          // if it is a new due date, set the current driver if present
          useEffect(() => {
            if (!dueDate?.id && !!currentAssignment?.carDriver) {
              setValue("driver", currentAssignment.carDriver.driver);
            }
          }, [currentAssignment?.carDriver?.id, dueDate?.id]);

          const isSwitchDisabled =
            currentAssignment &&
            ((!currentAssignment?.carDriver && !dueDate) || (!currentAssignment.carDriver && !dueDate?.visibleToUser));

          const isSelectDisabled =
            currentAssignment &&
            ((!currentAssignment?.carDriver && !dueDate) ||
              (!currentAssignment.carDriver && !dueDate?.visibleToUser) ||
              (!!dueDate &&
                ((!!dueDate.visibleToUser && !currentAssignment.carDriver) ||
                  (!dueDate.visibleToUser && !!currentAssignment.carDriver))) ||
              (!!dueDate?.visibleToUser && dueDate.visibleToUser.id == currentAssignment.carDriver?.driver.id) ||
              !dueDate);

          const isSettled = watch("isSettled");

          const visibleToUserChecked = watch("visibleToUser");

          return (
            <FormFieldsContainer>
              {!carId && (
                <Controller
                  name="car"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                      {({ labelId, isInvalid }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions<CarSearch, CarField>(
                          aborter,
                          searchTerm,
                          carsApi.getCars,
                          {
                            fields: CAR_FIELDS,
                          },
                        );

                        useEffect(() => {
                          if (!!isDisabled) return;
                          loadOptions(searchTerm.current).then(setDefaultCarOptions);
                        }, []);

                        return (
                          <AsyncSelect
                            menuPosition="fixed"
                            {...field}
                            inputId={labelId}
                            isLoading={isLoading}
                            isDisabled={dueDate?.isSettled || isLoading || isDisabled || !!dueDate}
                            defaultOptions={defaultCarOptions}
                            loadOptions={loadOptions}
                            placeholder={`${i18n.t.common.select}...`}
                            getOptionLabel={(x) =>
                              x.currentPlateNumber ?? x.lastPlateNumber ?? x.vehicleIdentificationNumber ?? ""
                            }
                            getOptionValue={(x) => x.id ?? ""}
                            styles={reactSelectStyles<Partial<Car>>(isInvalid)}
                            onChange={(v) => {
                              v ? loadCurrentAssigmment(v.id!) : setCurrentAssigment(undefined);
                              field.onChange(v);
                            }}
                          />
                        );
                      }}
                    </FormField>
                  )}
                />
              )}

              <Controller
                name="type"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.type} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={types.data?.items}
                        isLoading={isLoading}
                        isDisabled={dueDate?.isSettled || isDisabled || isLoading}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        styles={reactSelectStyles<DueDateType>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="date"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    const d = new Date(value!);
                    if (d.getFullYear() < 1000) {
                      return i18n.t.common.invalidDate;
                    }
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.date} error={fieldState.error}>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => !!v && setValue("date", v)}
                        value={watch("date")}
                        disabled={isDisabled || isLoading || dueDate?.isSettled}
                        isInvalid={isInvalid}
                      />
                    )}
                  </FormField>
                )}
              />

              <FormField label={i18n.t.common.visibleToUser} error={errors.isSettled} isOptional>
                {({ labelId }) => (
                  <Switch
                    id={labelId}
                    checked={visibleToUserChecked}
                    disabled={isSwitchDisabled}
                    onChange={(checked) => {
                      setValue("visibleToUser", checked);

                      if (checked && !watch("driver") && currentAssignment?.carDriver?.driver) {
                        setValue("driver", currentAssignment.carDriver.driver);
                      }
                    }}
                  />
                )}
              </FormField>
              <Controller
                name="driver"
                control={control}
                rules={{ required: watch("visibleToUser") }}
                render={({ field, fieldState }) => (
                  <FormField
                    label={i18n.t.users.singular}
                    error={fieldState.error}
                    isOptional={!watch("visibleToUser")}
                    showHelp={{ type: "info", onOpen: () => setIsHelpVisibleOpen(true) }}
                  >
                    {({ labelId, isInvalid }) => {
                      const drivers = [currentAssignment?.carDriver?.driver, dueDate?.visibleToUser].filter(
                        (driver) => driver !== null && driver !== undefined,
                      );
                      return (
                        <Select
                          menuPosition="fixed"
                          {...field}
                          inputId={labelId}
                          options={drivers}
                          isDisabled={isDisabled || isLoading || isSelectDisabled || !watch("visibleToUser")}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.fullName}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<User>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {!!dueDate && (
                <FormField label={i18n.t.common.isSettled} error={errors.isSettled} isOptional>
                  {({ labelId }) => (
                    <Switch
                      id={labelId}
                      disabled={isDisabled || isLoading}
                      checked={isSettled}
                      onChange={(checked) => setValue("isSettled", checked)}
                    />
                  )}
                </FormField>
              )}
            </FormFieldsContainer>
          );
        }}
      </Form>

      {isHelpVisibleOpen && (
        <ModalDialog onClose={() => setIsHelpVisibleOpen(false)} title={i18n.t.common.visibleToUser} isOnlyTextDialog>
          {i18n.t.common.visibleToUserInfo}
        </ModalDialog>
      )}
    </>
  );
};

export default DueDateFormNuovo;
